import { useEffect, useMemo, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useShownRoles } from "../../auth/useShownRoles";
import { useAuth } from "../../auth/AuthContext";
import { OperationsPrincipals, hasAnyRole } from "../../auth/roles";
import { QK, summitOpsApi } from "../../api/summitOpsApi";
import { useApiQuery } from "../../hooks/useApiQuery";
import { PageSkeleton } from "../Skeleton";
import { HubTabs, type HubTabItem } from "./HubTabs";
import { canSee, hubTitle, ownerOf, resolveOldRouteRedirect, type HubId } from "./hubs";

// The hub frame of the app shell: the tab strip of the hub that owns the current page, rendered once, above every page
// of that hub (detail pages too — /work-orders/J12345 shows "Operations Hub" as the current tab).

type Badge = Pick<HubTabItem, "badgeCount" | "badgeLabel" | "badgeTone">;

/** Live counts on tabs, only from endpoints the shell already calls (same query key — no extra request). */
export function useHubBadges(hubId: HubId | null, roles: readonly string[]): Readonly<Record<string, Badge>> {
  const notifications = useApiQuery([...QK.notifications], summitOpsApi.getNotifications, {
    enabled: hubId === "my" && hasAnyRole(roles, OperationsPrincipals),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000
  });

  return useMemo(() => {
    const map: Record<string, Badge> = {};
    const unread = (notifications.data ?? []).filter((n) => !n.isRead).length;
    if (hubId === "my" && unread > 0)
      map["my-notifications"] = {
        badgeCount: unread,
        badgeLabel: unread === 1 ? "unread notification" : "unread notifications",
        badgeTone: "brand"
      };
    return map;
  }, [hubId, notifications.data]);
}

/** One top-row section of a grouped hub: its own pages (the second row), or a single page shown on its own. */
interface HubSection {
  tab: HubTabItem;
  items: HubTabItem[];
}

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/**
 * Two-level hub navigation (owner 2026-10-23, the Summit Engineering pattern): when a hub's pages carry groups, the top
 * row shows one tab per group (it opens the group's first page the person may see) and a second row shows that group's
 * pages. A group with one page is a plain top-row tab. Hubs without groups keep one row.
 */
export function hubSections(tabs: readonly HubTabItem[]): HubSection[] | null {
  if (!tabs.some((t) => t.group)) return null;
  const sections: HubSection[] = [];
  for (const t of tabs) {
    const key = t.group ?? `page:${t.id}`;
    const last = sections[sections.length - 1];
    if (last && (last.tab.group ?? `page:${last.items[0]!.id}`) === key) last.items.push(t);
    else sections.push({ tab: { ...t }, items: [t] });
  }
  return sections.map(({ tab, items }) => {
    if (items.length === 1) return { tab: { ...items[0]!, group: undefined }, items };
    const count = items.reduce((n, i) => n + (typeof i.badgeCount === "number" ? i.badgeCount : 0), 0);
    return {
      items,
      tab: {
        id: `section-${slug(tab.group!)}`,
        to: items[0]!.to,
        label: tab.group!,
        icon: items[0]!.icon,
        badgeCount: count > 0 ? count : null,
        badgeLabel: items.find((i) => i.badgeLabel)?.badgeLabel,
        badgeTone: items.find((i) => i.badgeTone)?.badgeTone
      }
    };
  });
}

/** The hub strip above the page (when the page belongs to a hub the person can see), wrapping it in its tabpanel. */
export function HubFrame({ children }: { children: ReactNode }) {
  const roles = useShownRoles();
  const { pathname } = useLocation();
  const owner = ownerOf(pathname);
  const hub = owner && !owner.hub.standalone && canSee(owner.item, roles) ? owner.hub : null;
  const badges = useHubBadges(hub?.id ?? null, roles);

  const tabs = useMemo<HubTabItem[]>(
    () => (hub ? hub.items.filter((i) => canSee(i, roles)).map((i) => ({ id: i.id, to: i.to, label: i.label, icon: i.icon, group: i.group, ...badges[i.id] })) : []),
    [hub, roles, badges]
  );
  const sections = useMemo(() => hubSections(tabs), [tabs]);

  // Pages outside a hub (sign-in, not authorised) get the plain title.
  const standaloneTitle = owner?.hub.standalone && canSee(owner.item, roles) ? owner.item.label : null;
  useEffect(() => {
    if (!hub) document.title = hubTitle(standaloneTitle, null);
  }, [hub, standaloneTitle]);

  if (!hub || !owner) return <>{children}</>;
  if (!sections) {
    return (
      <HubTabs hubId={hub.id} label={`${hub.label} sections`} hubTitle={hub.label} tabs={tabs} activeId={owner.item.id} syncDocumentTitle variant="primary">
        {children}
      </HubTabs>
    );
  }

  const active = sections.find((s) => s.items.some((i) => i.id === owner.item.id)) ?? null;
  const topTabs = sections.map((s) => s.tab);
  if (!active || active.items.length === 1) {
    return (
      <HubTabs hubId={hub.id} label={`${hub.label} sections`} hubTitle={hub.label} tabs={topTabs} activeId={active?.tab.id ?? null} syncDocumentTitle variant="primary">
        {children}
      </HubTabs>
    );
  }
  return (
    <>
      <HubTabs hubId={hub.id} label={`${hub.label} sections`} hubTitle={hub.label} tabs={topTabs} activeId={active.tab.id} variant="primary" />
      <div className="-mt-2">
        <HubTabs
          hubId={`${hub.id}-${slug(active.tab.label)}`}
          label={`${active.tab.label} pages`}
          hubTitle={hub.label}
          tabs={active.items.map((i) => ({ ...i, group: undefined }))}
          activeId={owner.item.id}
          syncDocumentTitle
          variant="secondary"
        >
          {children}
        </HubTabs>
      </div>
    </>
  );
}

/** A hub's own URL (/shop-floor, /quality …): straight on to the person's first permitted tab, keeping query and hash. */
export function HubIndexRedirect({ path }: { path: string }) {
  const roles = useShownRoles();
  const { loading, principal } = useAuth();
  const location = useLocation();
  // Wait for the principal: deciding "first permitted tab" from an empty role list would send everyone to the start page.
  if (loading) return <PageSkeleton />;
  if (!principal) return <Navigate to="/sign-in" replace state={{ returnTo: `${location.pathname}${location.search}` }} />;
  const to = resolveOldRouteRedirect(path, roles) ?? "/";
  return <Navigate to={{ pathname: to, search: location.search, hash: location.hash }} state={location.state} replace />;
}

/** An old URL: on to the screen that replaced it, keeping the query string and hash. */
export function LegacyRouteRedirect({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search, hash: location.hash }} state={location.state} replace />;
}
